import { useState } from 'react';
import { Stack, Radio, RadioGroup, Heading, Icon } from '@chakra-ui/react';
import Wizard from '../Wizard';
import { ListingModel } from 'models';
import BasicListingCard from '../../Listing/BasicListingCard/BasicListingCard';
import { AiFillCheckCircle, AiFillCloseCircle } from "react-icons/ai";
import { JSONObject } from 'models/Api/types';
import { ListingStatus } from 'models/Listing/types';
import OriginalMarkingWarning from '../../Alert/OriginalMarkingWarning';

interface ListingDataSuggestionWizardProps {
	listing: ListingModel;
	isLoading?: boolean;
	onSubmit?: (listing: ListingModel, data: JSONObject) => void;
	onRetry?: () => void;
	hasErrors?: boolean;
	initialSelection?: 'ORIGINAL' | 'MULTIPLE_ITEMS' | 'FAKE' | 'REJECT';
}

export default function ListingDataSuggestionWizard({
	listing,
	isLoading = false,
	onSubmit = () => { },
	onRetry = () => { },
	hasErrors,
	initialSelection
}: ListingDataSuggestionWizardProps) {
	const [selectedOption, setSelectedOption] = useState(initialSelection || '');

	const onWizardSubmit = () => {
		let data = {};
		switch (selectedOption) {
			case 'ORIGINAL':
				data = { is_unique: true };
				break;
			case 'MULTIPLE_ITEMS':
				data = { multiple_items: true };
				break;
			case 'FAKE':
				data = { authentic: false };
				break;
			case 'REJECT':
				data = { processing_status: ListingStatus.REJECTED };
				break;
			default:
				break;
		}
		onSubmit(listing, data);
	}

	const getConfirmationCardOverrideConfig = () => {
		switch (selectedOption) {
			case 'ORIGINAL':
				return { isUnique: true };
			case 'MULTIPLE_ITEMS':
				return { multipleItems: true };
			case 'FAKE':
				return { fake: true };
			case 'REJECT':
				return { processing_status: ListingStatus.REJECTED };
			default:
				return {};
		}
	}

	const getConfirmationStepHeader = () => {
		switch (selectedOption) {
			case 'ORIGINAL':
				return 'The Sold Item is an Original (Non-Editioned)';
			case 'MULTIPLE_ITEMS':
				return 'This Sale Contains Multiple Items';
			case 'FAKE':
				return 'This Sale Contains a Fake Item';
			case 'REJECT':
				return 'Reject This Listing';
			default:
				return '';
		}
	}

	function renderRadioGroupSelection() {
		return (
			<RadioGroup onChange={setSelectedOption} value={selectedOption} textAlign={'left'}>
				<Stack direction="column" gap={2}>
					<Radio size="sm" value="ORIGINAL">
						<Heading size={'xs'}> The Sold Item is an Original (Non-Editioned)</Heading>
					</Radio>
					<Radio size="sm" value="MULTIPLE_ITEMS">
						<Heading size={'xs'}>This Sale Contains Multiple Items</Heading>
					</Radio>
					<Radio size="sm" value="FAKE">
						<Heading size={'xs'}>This Sale Contains a Fake Item</Heading>
					</Radio>
				</Stack>
			</RadioGroup>
		);
	}



	function renderRadioSelectionConfirmation() {
		return (
			<Stack gap={3}>
				<Heading fontSize={'sm'} textAlign={'center'}>{getConfirmationStepHeader()}</Heading>
				<OriginalMarkingWarning listings={[listing]} suggestionType={selectedOption as any} />
				<BasicListingCard listing={listing} showLabel={true} overrideConfig={getConfirmationCardOverrideConfig()} />
			</Stack>
		);
	}

	function renderWizardFinishingSuccessStep() {
		if (hasErrors) {
			return (
				<Stack gap={4} alignItems={'center'}>
					<Icon as={AiFillCloseCircle} color={'red.500'} w={10} h={10} />
					<Heading size={'xs'} textAlign={'center'}>
						Your Suggestion Could Not Be Submitted.<br />Please Try Again
					</Heading>
				</Stack>
			);
		} else {
			return (
				<Stack gap={4} alignItems={'center'}>
					<Icon as={AiFillCheckCircle} color={'green.500'} w={10} h={10} />
					<Heading size={'xs'}>
						Your Suggestion Has Been Submitted
					</Heading>
				</Stack>
			);
		}
	}


	const wizardSteps = initialSelection 
		? [{ label: 'Confirm Selection', content: renderRadioSelectionConfirmation() }]
		: [
			{ label: 'Select Option', content: renderRadioGroupSelection(), requiresValidation: true, isValidated: selectedOption !== '' },
			{ label: 'Confirm Selection', content: renderRadioSelectionConfirmation() }
		];

	return (
		<Stack gap={2} width={'100%'}>
			<BasicListingCard listing={listing} />
			<Wizard steps={wizardSteps} finishStep={renderWizardFinishingSuccessStep()} onSubmit={onWizardSubmit} loading={isLoading} />
		</Stack>
	);
}
